import axios from 'axios';
import Vue from 'vue';
import { ActionTree } from 'vuex';
import { SaveResult } from '@/types';
import { APIRoute, APIBaseRoute, EP } from '@/api-endpoints';
import { RootState } from '@/store/types';
import { User, UsersState, ResponseType } from '@/store/users/types';

export const actions: ActionTree<UsersState, RootState> = {
  // load user info
  loadUser({ commit }): Promise<void> {
    return new Promise<void>((resolve, reject) => {

      // Get validations based on view and action
      const url = APIRoute(EP.auth.show);
      axios.get(url).then((response: any) => {
        const responseData: any = response.data;
        // Pass value to mutation to place in store
        const user = responseData && responseData.user ? responseData.user : null;
        const currentUser = user ? user.user_id : undefined;
        commit('setUser', user);
        commit('setCurrentUser', currentUser, { root: true });
        resolve();
      }).catch((error: any) => {
        // check for response error code
        const response = error && error.response ? error.response : null;
        if (!response) reject(error);
        if (response.status == 401) {
          localStorage.removeItem('access_token');
          window.location.href = EP.auth.login;
          reject(error);
        } else if (response.status == 403) {
          reject(error);
        } else {
          reject(error);
        }
      });
    });
  },
  savePreferences({ commit, getters }, { preferences }): Promise<SaveResult> {
    return new Promise<SaveResult>((resolve, reject) => {
      if (preferences) {
        const payload = { data: JSON.stringify(preferences) };
        const method: any = axios.put;
        const ep: string = APIRoute(EP.users.preferences);
        method(ep, payload).then((response: any) => {
          const responseData: any = response.data;
          // Pass preferences to mutation
          const newPreferences = responseData && responseData.preferences ? responseData.preferences : undefined;
          commit('setPreferences', newPreferences);
          resolve({ success: true, responseData: response.data });
        }).catch((error: any) => {
          // Handle generic errors
          reject({ success: false, message: error });
        });
      } else {
        reject();
      }
    });
  },
  // load all possible user roles
  loadRoles({ commit, getters, rootState, rootGetters }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (rootGetters["users/isAdmin"]) {
        // Get validations based on view and action
        const url = APIRoute(EP.users.roles);
        axios.get(url).then((response: any) => {
          const responseData: any = response.data;
          // Pass value to mutation to place in store
          const roles = responseData && responseData.roles ? responseData.roles : null;
          commit('setRoles', roles);
          resolve();
        }).catch((error: any) => {
          console.warn(error);
          reject(error);
        });
      } else {
        commit('setRoles', []);
        resolve();
      }
    });
  },
  
  // access_token token methods
  readAccessToken({ commit }): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const token = localStorage.getItem('access_token') || undefined;
      resolve();
    });
  },
  removeAccessToken(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // remove access token and access type when logging out
      localStorage.removeItem('access_token');
      localStorage.removeItem('access_type');
      resolve();
    });
  },

  // store route for permissions
  setRoute({ commit }, route): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      commit('setRoute', route);
      resolve();
    });
  },

  // send logout request
  logout({ commit, dispatch }): Promise<SaveResult> {
    return new Promise<SaveResult>((resolve, reject) => {
      // dispatch('removeAccessToken');
      let endpoint_url;
      switch(localStorage.getItem('access_type')) {
        case 'oneid':
          endpoint_url = EP.auth.logout;
          break;
        case 'secondary_fusion_auth':
          endpoint_url = EP.secondaryFusionAuth.logout;
          break;
        default:
          // by default use one_id logout endpoint
          endpoint_url = EP.auth.logout;
          break;
      }
      const url = APIRoute(endpoint_url);

      axios.delete(url).then((response: any) => {
        const responseData = response.data;
        if (responseData.logout_redirect_uri) {
          window.location.href = responseData.logout_redirect_uri;
        } else {
          resolve({ success: true, responseData: responseData });
        }
      }).catch((error: any) => {
        console.warn(error);
        reject({ success: false, message: error });
      });
    });
  },
};
